import {
  format,
  parseISO,
  isValid,
  addDays,
  isBefore,
  isEqual,
  endOfWeek,
  endOfMonth,
  getWeek,
  getMonth,
} from 'date-fns';

// Mandatory base columns for Single Corporation Report
export const MANDATORY_COLUMNS = [
  { id: 'date', label: 'Date', align: 'left', minWidth: 110 },
  { id: 'employeeName', label: 'Employee Name', align: 'left', minWidth: 160 },
];

// Optional columns for Single Corporation Report
export const OPTIONAL_COLUMNS = [
  { id: 'punchIn', label: 'Punch In', align: 'center', minWidth: 100 },
  { id: 'punchOut', label: 'Punch Out', align: 'center', minWidth: 100 },
  { id: 'scheduledHrs', label: 'Scheduled Hrs', align: 'right', minWidth: 120 },
  { id: 'actualHrs', label: 'Actual Hours', align: 'right', minWidth: 120 },
  { id: 'overtimeHrs', label: 'Overtime Hrs', align: 'right', minWidth: 120 },
  { id: 'varianceHrs', label: 'Variance Hrs', align: 'right', minWidth: 120 },
  { id: 'varianceOvertimeHrs', label: 'Variance Hours /OT Hrs', align: 'right', minWidth: 160 },
  { id: 'otPercent', label: 'OT %', align: 'right', minWidth: 100 },
  { id: 'otRisk', label: 'OT Risk', align: 'center', minWidth: 110 },
  { id: 'status', label: 'Status', align: 'center', minWidth: 110 },
];

// Helper styles for clean enterprise table
export const cellBaseStyle = {
  fontSize: '0.7rem',
  padding: '8px 12px',
  borderRight: '1px solid #d9d9d9',
  borderBottom: '1px solid #d9d9d9',
  color: '#222222',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const subHeaderRowStyle = {
  backgroundColor: '#81d3f6',
  color: 'black',
  fontWeight: 700,
  fontSize: '0.85rem',
  padding: '8px 12px',
  borderRight: '1px solid #d9d9d9',
  borderBottom: '1px solid #d9d9d9',
  borderTop: '1px solid #d9d9d9',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

export const headerCellStyle = {
  ...cellBaseStyle,
  backgroundColor: '#f5f5f5',
  color: '#222222',
  fontWeight: 700,
  fontSize: '0.78rem',
  textTransform: 'none',
  borderBottom: '1px solid #d9d9d9',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

export const footerCellStyle = {
  ...cellBaseStyle,
  backgroundColor: '#f5f5f5',
  fontWeight: 700,
  fontSize: '0.78rem',
  color: '#222222',
  borderTop: '1px solid #d9d9d9',
};

export const grandTotalCellStyle = {
  ...cellBaseStyle,
  backgroundColor: '#f1f5f9',
  fontWeight: 800,
  fontSize: '0.75rem',
  color: '#0f172a',
  borderTop: '2px solid #cbd5e1',
  borderBottom: '2px solid #cbd5e1',
};

// Helper to format ISO time strings to 12-hour AM/PM format
export const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-' || timeStr === 'null' || timeStr === 'undefined') return '-';
  const str = String(timeStr).trim();

  // If already like "09:00 AM" or "9:00 PM"
  if (/^\d{1,2}:\d{2}(\s?[APap][Mm])$/.test(str)) {
    return str.toUpperCase();
  }

  // If like "09:00:00" or "09:00" (time only without date)
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(str)) {
    const [hStr, mStr] = str.split(':');
    let h = parseInt(hStr, 10);
    const m = mStr.padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
  }

  try {
    let d = parseISO(str);
    if (!isValid(d)) {
      d = parseISO(str.replace(' ', 'T'));
    }
    if (!isValid(d)) {
      d = new Date(str);
    }
    if (isValid(d)) {
      return format(d, 'hh:mm a');
    }
    return str;
  } catch {
    return str;
  }
};

// Helper to format ISO date strings to MM-dd-yyyy
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = parseISO(dateStr);
    if (isValid(d)) {
      return format(d, 'MM-dd-yyyy');
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

// Mapper from API GridDetails item to Table row structure
export const mapGridDetailToRow = (item, index) => {
  const sched = Number(item.ScheduledHours ?? item.scheduledHrs ?? 0);
  const actual = Number(item.ActualHours ?? item.actualHrs ?? 0);
  const ot = Number(item.OTHours ?? item.overtimeHrs ?? 0);
  const varH = Number(item.VarianceHours ?? item.varianceHrs ?? 0);

  let otPercent = item.OTPercent ?? item.otPercent;
  if (otPercent !== undefined && otPercent !== null) {
    if (typeof otPercent === 'number') {
      otPercent = `${otPercent.toFixed(2)}%`;
    } else if (typeof otPercent === 'string' && !otPercent.includes('%')) {
      const num = Number(otPercent);
      otPercent = !isNaN(num) ? `${num.toFixed(2)}%` : otPercent;
    }
  } else {
    otPercent = actual > 0 ? `${((ot / actual) * 100).toFixed(2)}%` : '0.00%';
  }

  let otRisk = item.OTRisk || item.otRisk;
  if (!otRisk) {
    otRisk = ot > 2 ? 'High' : ot > 0 ? 'Medium' : 'Low';
  }

  return {
    id: `${item.EmployeeId || 'emp'}-${item.CorporationID || ''}-${item.NimbleDeptID || ''}-${item.NimbleJobID || ''}-${item.Week || ''}-${item.Month || ''}-${item.Date || ''}-${item.WeekStartDate || ''}-${index}`,
    employeeId: item.EmployeeId,
    date: formatDate(item.Date || item.WeekStartDate),
    employeeName: item.EmployeeName || (item.EmployeeCount !== undefined && item.EmployeeCount !== null ? String(item.EmployeeCount) : '-'),
    employeeCount: item.EmployeeCount,
    department: item.NimbleDeptName || item.DepartmentName || item.department || '-',
    departmentId: item.NimbleDeptID || item.DepartmentID || item.departmentId,
    jobTitle: item.NimbleJobName || item.JobName || item.jobTitle || '-',
    jobId: item.NimbleJobID || item.JobID || item.jobId,
    punchIn: formatTime(item.PunchIn || item.punchIn),
    punchOut: formatTime(item.PunchOut || item.punchOut),
    scheduledHrs: sched,
    actualHrs: actual,
    overtimeHrs: ot,
    varianceHrs: varH,
    otPercent: otPercent,
    otRisk: otRisk,
    status: item.Status || item.status || 'On Schedule',
    week: item.Week ?? item.week ?? item.WeekName ?? item.weekName,
    weekStartDate: item.WeekStartDate ?? item.weekStartDate ?? item.WeekStart ?? item.weekStart,
    weekEndDate: item.WeekEndDate ?? item.weekEndDate ?? item.WeekEnd ?? item.weekEnd,
    month: item.Month ?? item.month ?? item.MonthName ?? item.monthName,
    monthStartDate: item.MonthStartDate ?? item.monthStartDate ?? item.MonthStart ?? item.monthStart,
    monthEndDate: item.MonthEndDate ?? item.monthEndDate ?? item.MonthEnd ?? item.monthEnd,
    year: item.Year ?? item.year,
    corporationName: item.CorporationName,
    pcName: item.PCName,
  };
};

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const formatMonthHeader = (rawMonth, startDateStr, rawYear) => {
  let monthName = 'January';
  let yearStr = rawYear ? String(rawYear).trim() : '';

  if (startDateStr) {
    try {
      const d = parseISO(startDateStr);
      if (isValid(d)) {
        monthName = format(d, 'MMMM');
        if (!yearStr) yearStr = format(d, 'yyyy');
      }
    } catch { }
  } else if (rawMonth) {
    const str = String(rawMonth).trim();
    const num = parseInt(str.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(num) && num >= 1 && num <= 12) {
      monthName = MONTH_NAMES[num - 1];
    } else {
      const matchMonth = MONTH_NAMES.find(
        (m) => m.toLowerCase().startsWith(str.toLowerCase())
      );
      monthName = matchMonth || str;
    }
  }

  if (yearStr) {
    return `${monthName}, ${yearStr}`;
  }
  return monthName;
};

export const formatWeekHeader = (rawWeek, startDateStr, endDateStr) => {
  let weekCode = 'W01';
  if (rawWeek) {
    const num = parseInt(String(rawWeek).replace(/[^0-9]/g, ''), 10);
    if (!isNaN(num)) {
      weekCode = `W${String(num).padStart(2, '0')}`;
    } else {
      weekCode = String(rawWeek);
    }
  } else if (startDateStr) {
    try {
      const d = parseISO(startDateStr);
      if (isValid(d)) weekCode = `W${String(getWeek(d, { weekStartsOn: 0 })).padStart(2, '0')}`;
    } catch { }
  }

  let startFormatted = '';
  let endFormatted = '';
  if (startDateStr) {
    try {
      const startD = parseISO(startDateStr);
      if (isValid(startD)) startFormatted = format(startD, 'MMM dd, yyyy');
    } catch { }
  }
  if (endDateStr) {
    try {
      const endD = parseISO(endDateStr);
      if (isValid(endD)) endFormatted = format(endD, 'MMM dd, yyyy');
    } catch { }
  }

  if (startFormatted && endFormatted) {
    return `${weekCode} (${startFormatted} – ${endFormatted})`;
  }
  if (startFormatted) {
    return `${weekCode} (${startFormatted})`;
  }
  return weekCode;
};

/**
 * Generate exact calendar week groups (Sunday to Saturday) bounded by fromDate and toDate for Single Corporation
 */
export const generateCalendarWeekGroups = (
  fromStr,
  toStr,
  deptLabel,
  jobLabel,
  customData
) => {
  const groups = [];

  let start = isValid(parseISO(fromStr)) ? parseISO(fromStr) : new Date(2026, 0, 1);
  const end = isValid(parseISO(toStr)) ? parseISO(toStr) : new Date(2026, 0, 31);

  while (isBefore(start, end) || isEqual(start, end)) {
    const calendarWeekEnd = endOfWeek(start, { weekStartsOn: 0 });
    const weekEnd = isBefore(calendarWeekEnd, end) ? calendarWeekEnd : end;

    // Block-scoped constants to prevent ESLint no-loop-func warnings
    const periodStart = start;
    const periodEnd = weekEnd;

    const startFormatted = format(periodStart, 'MMM dd, yyyy');
    const endFormatted = format(periodEnd, 'MMM dd, yyyy');
    const weekNumber = getWeek(periodStart, { weekStartsOn: 0 });
    const weekCode = `W${String(weekNumber).padStart(2, '0')}`;

    const weekTitle = `${weekCode} (${startFormatted} – ${endFormatted})`;
    const headerTitle = `${weekCode} (${startFormatted} – ${endFormatted})`;

    let weekRecords = [];

    if (customData && customData.length > 0) {
      weekRecords = customData.filter((r) => {
        const recordDate = parseISO(r.date);
        return (
          isValid(recordDate) &&
          (isEqual(recordDate, periodStart) || isBefore(periodStart, recordDate)) &&
          (isEqual(recordDate, periodEnd) || isBefore(recordDate, periodEnd))
        );
      });
    }

    if (weekRecords.length > 0) {
      groups.push({
        id: `week-${weekNumber}-${format(periodStart, 'yyyyMMdd')}`,
        headerTitle,
        weekTitle,
        records: weekRecords,
      });
    }

    start = addDays(weekEnd, 1);
  }

  return groups;
};

/**
 * Generate separate monthly groups for the date range for Single Corporation
 */
export const generateMonthGroups = (
  fromStr,
  toStr,
  deptLabel,
  jobLabel,
  customData,
  isSingleCorp = true
) => {
  const groups = [];

  let start = isValid(parseISO(fromStr)) ? parseISO(fromStr) : new Date(2026, 0, 1);
  const end = isValid(parseISO(toStr)) ? parseISO(toStr) : new Date(2026, 0, 31);

  while (isBefore(start, end) || isEqual(start, end)) {
    const calendarMonthEnd = endOfMonth(start);
    const monthEnd = isBefore(calendarMonthEnd, end) ? calendarMonthEnd : end;

    // Block-scoped constants to prevent ESLint no-loop-func warnings
    const periodStart = start;
    const periodEnd = monthEnd;

    const startFormatted = format(periodStart, 'MMM dd, yyyy');
    const endFormatted = format(periodEnd, 'MMM dd, yyyy');
    const monthNumber = getMonth(periodStart) + 1;
    const monthCode = `M${String(monthNumber).padStart(2, '0')}`;
    const monthName = format(periodStart, 'MMMM');
    const yearStr = format(periodStart, 'yyyy');

    const monthTitle = `${monthName}, ${yearStr}`;
    const headerTitle = isSingleCorp
      ? `${monthName}, ${yearStr}`
      : `${monthCode} (${startFormatted} – ${endFormatted})`;

    let monthRecords = [];

    if (customData && customData.length > 0) {
      monthRecords = customData.filter((r) => {
        const recordDate = parseISO(r.date);
        return (
          isValid(recordDate) &&
          (isEqual(recordDate, periodStart) || isBefore(periodStart, recordDate)) &&
          (isEqual(recordDate, periodEnd) || isBefore(recordDate, periodEnd))
        );
      });
    }

    if (monthRecords.length > 0) {
      groups.push({
        id: `month-${monthNumber}-${format(periodStart, 'yyyyMM')}`,
        headerTitle,
        monthTitle,
        records: monthRecords,
      });
    }

    start = addDays(monthEnd, 1);
  }

  return groups;
};

/**
 * Format Department and Job Title labels
 */
export const getFormattedLabels = (filters) => {
  const rawDept = filters?.department || 'all';
  const rawJob = filters?.jobTitle || 'all';

  const deptLabel =
    rawDept === 'all'
      ? 'All'
      : rawDept.charAt(0).toUpperCase() + rawDept.slice(1);

  const jobLabel =
    rawJob === 'all'
      ? 'All'
      : rawJob.charAt(0).toUpperCase() + rawJob.slice(1);

  return { deptLabel, jobLabel };
};
